import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ExtendSessionDialogComponent } from 'src/app/dialogs/extend-session-dialog/extend-session-dialog.component';
import { NewAppDialogComponent } from '../../dialogs/new-app-dialog/new-app-dialog.component';
import {
  UserManagementDialogComponent,
  UserManagementDialogData,
} from 'src/app/dialogs/user-management-dialog/user-management-dialog.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import {
  EditGroupDialogComponent,
  EditGroupDialogData,
} from 'src/app/dialogs/edit-group-dialog/edit-group-dialog.component';
import {
  MakePaymentDialogComponent,
  MakePaymentDialogData,
} from 'src/app/dialogs/make-payment-dialog/make-payment-dialog.component';
import { SuspensionDialogComponent } from 'src/app/dialogs/suspension-dialog/suspension-dialog.component';
import { CreateGroupDialogComponent } from 'src/app/dialogs/create-group-dialog/create-group-dialog.component';
import { Authorization } from 'src/app/model/Authorization';
import { CaptureAuthorizationDialogComponent } from 'src/app/dialogs/capture-authorization-dialog/capture-authorization-dialog.component';
import { VoidAuthorizationDialogComponent } from 'src/app/dialogs/void-authorization-dialog/void-authorization-dialog.component';
import {
  ReviewOfferDialogComponent,
  ReviewOfferDialogResult,
  ReviewOfferDialogData,
} from 'src/app/dialogs/review-offer-dialog/review-offer-dialog.component';
import { TransactionRequest } from 'src/app/model/TransactionRequest';
import {
  BuildOfferDialogComponent,
  BuildOfferDialogData,
} from 'src/app/dialogs/build-offer-dialog/build-offer-dialog.component';
import { Customer } from 'src/app/model/Customer';
import { RefundAuthorizationDialogComponent } from 'src/app/dialogs/refund-authorization-dialog/refund-authorization-dialog.component';
import {
  TransactionAuthorizationDialogComponent,
  TransactionAuthorizationDialogData,
} from 'src/app/dialogs/transaction-authorization-dialog/transaction-authorization-dialog.component';
import {
  TransactionRequestOfferDialogComponent,
  TransactionRequestOfferDialogData,
} from 'src/app/dialogs/transaction-request-offer-dialog/transaction-request-offer-dialog.component';
import { CancelRequestDialogComponent } from 'src/app/dialogs/cancel-request-dialog/cancel-request-dialog.component';
import {
  EditPaymentDialogComponent,
  EditPaymentDialogData,
  EditPaymentDialogResponse,
} from 'src/app/dialogs/edit-payment-dialog/edit-payment-dialog.component';
import { TransactionDetail } from 'src/app/model/TransactionDetail';
import { NLSTransaction } from 'src/app/model/NLSTransaction';
import {
  SettingsModalComponent,
  SettingsModalData,
} from 'src/app/components/settings-modal/settings-modal.component';
import { InviteUserDialogComponent } from 'src/app/dialogs/invite-user-dialog/invite-user-dialog.component';
import { RefundLoanDialogComponent } from 'src/app/dialogs/refund-loan-dialog/refund-loan-dialog.component';
import { TransactionEvent } from 'src/app/model/TransactionEvent';
import { TosViewerDialogComponent } from 'src/app/dialogs/tos-viewer-dialog/tos-viewer-dialog.component';
import {
  ReviewRequestDialogResult,
  ReviewRequestDialogComponent,
  ReviewRequestDialogData,
} from '../../dialogs/review-request-dialog/review-request-dialog.component';
import {
  SubmitRequestDialogComponent,
  SubmitRequestDialogData,
} from '../../dialogs/submit-request-dialog/submit-request-dialog.component';

/**
 * Dialog service handles opening all dialogs in the application. All dialog openings
 * return an observable on completion
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService implements OnDestroy {
  /** Dependency injections */
  public dialog: MatDialog = inject(MatDialog);
  private authService: AuthService = inject(AuthService);

  /** List of all currently opened dialog references */
  private openDialogRefs: MatDialogRef<any>[] = [];

  /** Subscription to authentication status changes */
  private authSubscription: Subscription | null = null;

  /**
   * Constructor sets up the dialog service to authentication status
   */
  constructor() {
    // Subscribe to authentication status changes
    this.authSubscription = this.authService.isAuthenticated$.subscribe(
      (isAuthenticated) => {
        // If user is no longer authenticated and there are open dialogs, close them all
        if (!isAuthenticated && this.openDialogRefs.length > 0) {
          console.log('Closing all open dialogs due to authentication loss');
          this.openDialogRefs.forEach((dialogRef) => {
            dialogRef.close();
          });
          this.openDialogRefs = [];
        }
      },
    );
  }

  /**
   * Helper method to track dialog references and return observable with cleanup
   */
  private trackDialogRef<T, R>(dialogRef: MatDialogRef<T>): Observable<R> {
    // Track the dialog ref
    this.openDialogRefs.push(dialogRef);

    // Return observable that cleans up when dialog closes
    return dialogRef.afterClosed().pipe(
      tap(() => {
        const index = this.openDialogRefs.indexOf(dialogRef);
        if (index > -1) {
          this.openDialogRefs.splice(index, 1);
        }
      }),
    );
  }

  /**
   * Cleanup method to unsubscribe from authentication status changes
   */
  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  /**
   * Open up the extend session dialog in the middle of the screen
   */
  public openExtendSession(): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(ExtendSessionDialogComponent, {
      // width: "30vw",
      // height: "50vh",
      disableClose: true,
    });

    return this.trackDialogRef(dialogRef);
  }

  public openNewApp(): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(NewAppDialogComponent, {
      width: '30vw',
      height: '100vh',
      position: { right: '0', top: '0' },
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the Credit Terms & Conditions dialog
   */
  public openTosViewerDialog(): Observable<void> {
    const dialogRef = this.dialog.open(TosViewerDialogComponent, {
      width: '1600px',
      maxWidth: '95vw',
      panelClass: 'tos-dialog-font-scope',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open up the user management dialog
   */
  public openUserManagementDialog(
    data: UserManagementDialogData,
  ): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(UserManagementDialogComponent, {
      maxWidth: '50vw',
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: data,
      disableClose: false,
      panelClass: 'user-management-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open up a generic confirmation dialog
   */
  public openConfirmationDialog(
    data: ConfirmationDialogData,
  ): Observable<boolean> {
    // Open the dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '550px',
      maxWidth: '70vw',
      data: data,
      disableClose: true,
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open up the edit group dialog
   */
  public openEditGroupDialog(data: EditGroupDialogData): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(EditGroupDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: data,
      disableClose: false,
      panelClass: 'edit-group-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open up the make payment dialog
   */
  public openMakePaymentDialog(data: MakePaymentDialogData): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(MakePaymentDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: data,
      disableClose: false,
      panelClass: 'make-payment-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open up the suspension dialog to inform users about their suspended account status
   */
  public openSuspensionDialog(): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(SuspensionDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true, // Prevent closing by clicking outside or ESC
      panelClass: 'suspension-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the create group dialog
   */
  public openCreateGroupDialog(): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(CreateGroupDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'create-group-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the capture authorization dialog
   *
   * @param authorization Provided authorization
   * @return Observable on dialog close
   */
  public openCaptureAuthorizationDialog(
    authorization: Authorization,
  ): Observable<{ success: boolean }> {
    // Open the dialog
    const dialogRef = this.dialog.open(CaptureAuthorizationDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { authorization: authorization },
      disableClose: false,
      panelClass: 'capture-authorization-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the void authorization dialog
   *
   * @param authorization Provided authorization
   * @return Observable on dialog close
   */
  public openVoidAuthorizationDialog(
    authorization: Authorization,
  ): Observable<{ success: boolean }> {
    // Open the dialog
    const dialogRef = this.dialog.open(VoidAuthorizationDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { authorization: authorization },
      disableClose: false,
      panelClass: 'void-authorization-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the refund transaction dialog
   *
   * @param authorization Authorization to submit refund for
   * @return Observable on dialog close
   */
  public openRefundAuthorizationDialog(
    authorization: Authorization,
  ): Observable<{ success: boolean }> {
    // Open the dialog
    const dialogRef = this.dialog.open(RefundAuthorizationDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { authorization: authorization },
      disableClose: false,
      panelClass: 'refund-authorization-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the refund transaction dialog
   *
   * @param authorization Authorization to submit refund for
   * @return Observable on dialog close
   */
  public openRefundLoanDialog(
    transactionevent: TransactionEvent,
  ): Observable<{ success: boolean }> {
    // Log the Transaction Event
    console.log(
      'Opening Refund Loan Dialog for Transaction Event:',
      transactionevent,
    );

    // Open the dialog
    const dialogRef = this.dialog.open(RefundLoanDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { transactionevent: transactionevent },
      disableClose: false,
      panelClass: 'refund-loan-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the review request dialog
   *
   * @param transactionRequest Transaction request to review
   * @return Observable on dialog close
   */
  public openReviewRequestDialog(
    transactionRequest: TransactionRequest,
  ): Observable<ReviewRequestDialogResult> {
    // Open the dialog
    const dialogRef = this.dialog.open(ReviewRequestDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { transactionRequest } as ReviewRequestDialogData,
      disableClose: false,
      panelClass: 'review-request-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the submit request dialog for a specific customer
   *
   * @param customer Customer to submit the request for
   * @return Observable on dialog close
   */
  public openSubmitRequestDialog(
    customer: Customer,
  ): Observable<{ success: boolean }> {
    // Open the dialog
    const dialogRef = this.dialog.open(SubmitRequestDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { customer } as SubmitRequestDialogData,
      disableClose: false,
      panelClass: 'submit-request-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the review offer dialog
   *
   * @param transactionRequest Transaction request to review
   * @return Observable on dialog close
   */
  public openReviewOfferDialog(
    transactionRequest: TransactionRequest,
  ): Observable<ReviewOfferDialogResult> {
    // Open the dialog
    const dialogRef = this.dialog.open(ReviewOfferDialogComponent, {
      width: '470px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { transactionRequest } as ReviewOfferDialogData,
      disableClose: false,
      panelClass: 'review-offer-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the build offer dialog for a specific customer
   *
   * @param customer Customer to submit the request for
   * @return Observable on dialog close
   */
  public openBuildOfferDialog(
    customer: Customer,
  ): Observable<{ success: boolean }> {
    // Open the dialog
    const dialogRef = this.dialog.open(BuildOfferDialogComponent, {
      width: '480px',
      height: '100vh',
      position: { right: '0', top: '0' },
      data: { customer } as BuildOfferDialogData,
      disableClose: false,
      panelClass: 'build-offer-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the transaction authorization dialog. The dialog fetches and displays the
   * Transaction Authorization Agreement (TAA) HTML from the backend for the given transaction.
   *
   * @param dialogData Contains the NLS transaction ID (acctrefno) for the TAA to display
   * @return Observable on dialog close
   */
  public openTransactionAuthorizationDialog(
    dialogData: TransactionAuthorizationDialogData,
  ): Observable<void> {
    // Open the dialog
    const dialogRef = this.dialog.open(
      TransactionAuthorizationDialogComponent,
      {
        data: dialogData,
        disableClose: false,
        panelClass: 'transaction-authorization-dialog-container',
      },
    );

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the transaction request offer (TAA) dialog.
   * Fetches and displays the Transaction Authorization Agreement HTML for the given accepted transaction.
   *
   * @param data Must include transactionRequestId (transaction request id)
   * @return Observable on dialog close
   */
  public openTransactionRequestOfferDialog(
    data: TransactionRequestOfferDialogData,
  ): Observable<void> {
    const dialogRef = this.dialog.open(TransactionRequestOfferDialogComponent, {
      data,
      disableClose: false,
      panelClass: 'transaction-request-offer-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the cancel request dialog
   *
   * @param requestId ID of the transaction request to cancel
   * @return Observable on dialog close
   */
  public openCancelRequestDialog(requestId: string): Observable<void> {
    const dialogRef = this.dialog.open(CancelRequestDialogComponent, {
      data: { requestId },
      disableClose: false,
      panelClass: 'cancel-request-dialog-container',
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the edit pending payment dialog
   *
   * @param transactionDetail Transaction detail to edit
   * @returns Observable on dialog close
   */
  public openEditPendingPaymentDialog(
    nlsTransaction: NLSTransaction,
    transactionDetail: TransactionDetail,
  ): Observable<EditPaymentDialogResponse | undefined> {
    const data: EditPaymentDialogData = {
      transaction: nlsTransaction,
      payment: transactionDetail,
    };

    const dialogRef = this.dialog.open(EditPaymentDialogComponent, {
      width: '500px',
      height: '100vh',
      position: { right: '0', top: '0' },
      panelClass: 'slide-out-dialog',
      data: data,
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the settings modal dialog
   *
   * @param data Optional initial sidebar selection (e.g. Important Links).
   */
  public openSettingsModal(data?: SettingsModalData): Observable<any> {
    const dialogRef = this.dialog.open(SettingsModalComponent, {
      width: '90vw', // modal takes 90% of viewport width
      maxWidth: '800px', // but never exceeds 800px
      height: 'auto', // auto height so it can grow or shrink with content
      maxHeight: '90vh', // maximum 90% of viewport height
      minHeight: '65vh',
      panelClass: 'custom-settings-modal',
      ...(data != null ? { data } : {}),
    });

    return this.trackDialogRef(dialogRef);
  }

  /**
   * Open the invite user dialog
   */
  public openInviteUserDialog(): Observable<void> {
    const dialogRef = this.dialog.open(InviteUserDialogComponent, {
      width: '400px',
      height: '100%',
      position: { top: '0', right: '0' },
      panelClass: 'invite-user-dialog-container',
      backdropClass: 'invite-user-backdrop',
    });

    return this.trackDialogRef(dialogRef);
  }
}
